/* eslint-disable no-empty */
import path from 'path-browserify';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import AmbraContainer from '../components/ambraContainer/AmbraContainer';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ImageWrapper from '../components/common/ImageWrapper';
import VideoWrapper from '../components/common/VideoWrapper';
import ObjViewer from '../components/objViewer/ObjViewer';
import PdfViewer from '../components/pdfViewer/PdfViewer';
import AddResource, { documentFormats, imageFormats, threeFormats, videoFormats } from '../components/resources/AddResource';
import UploadProgress from '../containers/upload/UploadProgress';
import { DCM, DOCS, IMAGE, resourcePrivacy, SELFHELP, SUPPLEMENTARY, THREE, VIDEO } from '../models/Resource';
import parseFiles from '../utils/dicomUtils';
import { megaBytesToBytes, sanitizeFileName } from '../utils/files';

const DEFAULT_CRITERIA = {
  category: 'educational,supplementary,self-help',
  limit: 10,
  page: 0,
  type: null,
};

export default class ResourcesBloc {
  isAddingResource$ = new BehaviorSubject(false);
  isEditingResource$ = new BehaviorSubject(false);
  loadingResourceInfo$ = new BehaviorSubject(null);

  criteria$ = new BehaviorSubject(DEFAULT_CRITERIA);
  selfHelpCategories$ = new BehaviorSubject([]);

  newResources$ = new BehaviorSubject([]);

  constructor({
    appRepository,
    drawerRepository,
    materialRepository,
    dialogRepository,
    errorRepository,
    notificationsRepository,
    resourceRepository,
    selfHelpCategoryRepository,
    uploadProgressRepository,
    userRepository,
    patientRepository,
    authRepository,
  }) {
    this.appRepository = appRepository;
    this.drawerRepository = drawerRepository;
    this.materialRepository = materialRepository;
    this.dialogRepository = dialogRepository;
    this.errorRepository = errorRepository;
    this.notificationRepository = notificationsRepository;
    this.resourceRepository = resourceRepository;
    this.selfHelpCategoryRepository = selfHelpCategoryRepository;
    this.uploadProgressRepository = uploadProgressRepository;
    this.userRepository = userRepository;
    this.patientRepository = patientRepository;
    this.authRepository = authRepository;

    this.loadInitialData();
    this.getSelfHelpCategories();
  }

  get userWorkspace$() {
    return this.authRepository.userWorkspace$;
  }

  getMaterials() {
    return this.materialRepository.getMaterials(this.criteria$.getValue());
  }

  async getSelfHelpCategories() {
    try {
      const selfHelpCategories = await this.selfHelpCategoryRepository.getCategories();
      this.selfHelpCategories$.next(selfHelpCategories);
    } catch (error) {}
  }

  moveOtherToLast(arr) {
    const result = arr.filter((e) => e !== 'Other').slice();
    result.push(arr.find((e) => e === 'Other'));
    result.filter((e) => e);
    return result;
  }

  async openAddResource() {
    const isMediphanyWorkspace = this.userWorkspace$.getValue()?.isMediphanyWorkspace;

    this.isAddingResource$.next(false);
    await this.materialRepository.getTags();
    await this.getSelfHelpCategories();

    this.drawerRepository.loadDrawer(AddResource, {
      anatomies: this.moveOtherToLast(this.anatomies$.getValue()).map((e) => ({ label: e, value: e })),
      isMediphanyWorkspace,
      isSubmitting$: this.isAddingResource$,
      modalities: this.moveOtherToLast(this.modalities$.getValue()).map((e) => ({ label: e, value: e })),
      onAddResource: async (data) => {
        if (!data.files) {
          this.errorRepository.error$.next({
            message: 'Resource file was not chosen',
            severity: 'error',
          });
          return;
        }

        this.isAddingResource$.next(true);
        try {
          await this.addResource(data);
          this.drawerRepository.toggleDrawer(false);
          this.errorRepository.error$.next({
            message: 'Resource file is being processed and will be available in a few minutes',
            severity: 'success',
          });
          this.isAddingResource$.next(false);
        } catch (error) {
          this.isAddingResource$.next(false);
          // eslint-disable-next-line no-console
          console.log('ResourcesBloc - onAddResource error:', error);
        }
      },
      onValidateFile: (file, type, category) => this.validateFile(file, type, category),
      selfHelpCategories: this.selfHelpCategories$.getValue(),
      tags: this.tags$.getValue(),
    });
  }

  async openEditResource(resource) {
    if (!resource.isReady) {
      this.errorRepository.error$.next({
        message: 'Failed to edit file, this resource is not ready!',
        severity: 'error',
      });
      return;
    }

    const isMediphanyWorkspace = this.userWorkspace$.getValue()?.isMediphanyWorkspace;

    if (!isMediphanyWorkspace && resource?.privacy === resourcePrivacy.GLOBAL) {
      this.errorRepository.error$.next({
        message: "You cannot edit 'Global Mediphany' resources",
        severity: 'error',
      });
      return;
    }

    this.isEditingResource$.next(false);
    await this.materialRepository.getTags();
    await this.getSelfHelpCategories();

    this.drawerRepository.loadDrawer(AddResource, {
      anatomies: this.moveOtherToLast(this.anatomies$.getValue()).map((e) => ({ label: e, value: e })),
      isMediphanyWorkspace,
      isSubmitting$: this.isAddingResource$,
      modalities: this.moveOtherToLast(this.modalities$.getValue()).map((e) => ({ label: e, value: e })),
      onAddResource: async (data) => {
        try {
          this.isEditingResource$.next(true);
          await this.editResource(data, resource.id);
          this.drawerRepository.toggleDrawer(false);
        } finally {
          this.isEditingResource$.next(false);
        }
      },
      onValidateFile: (file, type, category) => this.validateFile(file, type, category),
      resource,
      selfHelpCategories: this.selfHelpCategories$.getValue(),
      tags: this.tags$.getValue(),
    });
  }

  async editResource(data, id) {
    const payload = {
      category: data?.data?.category,
      description: data?.data?.description,
      file: data?.file,
      privacy: data?.data?.privacy,
      thumbnail: data?.thumbnail,
      title: data?.data?.title,
      type: data?.data?.type,
    };

    if (data?.customThumbnailPath) payload.customThumbnailPath = data.customThumbnailPath;
    if (data?.customThumbnailPath === '') payload.customThumbnailPath = '';

    if (payload.category === SUPPLEMENTARY) {
      payload.modality = data?.data?.modality;
      payload.anatomy = data?.data?.anatomy;
    } else {
      payload.tags = data?.tags;
    }

    if (payload.category === SELFHELP) {
      payload.selfHelpCategoryName = data.selfHelpCategoryName;
    }

    try {
      this.uploadProgressRepository.start();
      this.dialogRepository.loadDialog(UploadProgress, { preventClose: true });

      await this.materialRepository.updateMaterial(payload, id, this.uploadProgressRepository.progress$);
      await this.getMaterials();

      this.uploadProgressRepository.finish();
    } catch (error) {
      this.uploadProgressRepository.fail();
      throw error;
    }
  }

  async addResource(data) {
    const payload = {
      category: data?.data?.category,
      description: data?.data?.description,
      files: data?.files,
      privacy: data?.data?.privacy,
      thumbnail: data?.thumbnail,
      title: data?.data?.title,
      type: data?.data?.type,
    };

    if (payload.category === SUPPLEMENTARY) {
      payload.modality = data?.data?.modality;
      payload.anatomy = data?.data?.anatomy;
    } else {
      payload.tags = data?.tags;
    }

    if (payload.category === SELFHELP) {
      payload.selfHelpCategoryName = data.selfHelpCategoryName;
    }

    try {
      this.uploadProgressRepository.start();
      this.dialogRepository.loadDialog(UploadProgress, { preventClose: true });

      await this.materialRepository.createMaterial(payload, this.uploadProgressRepository.progress$);
      await this.getMaterials();

      this.uploadProgressRepository.finish();
    } catch (error) {
      this.uploadProgressRepository.fail();
      throw error;
    }
  }

  async validateFile(files, type) {
    const allTypes = [...imageFormats, ...videoFormats, ...threeFormats, ...documentFormats];
    if (type === DCM) {
      try {
        const status = await parseFiles(files);
        return {
          error: false,
          message: `${status.imageCount} image${status.imageCount > 1 ? 's' : ''}`,
        };
      } catch (e) {
        return {
          error: true,
          message: e.message,
        };
      }
    }

    const file = sanitizeFileName(files[0]);

    switch (type) {
      case IMAGE:
        if (file.size > megaBytesToBytes(5)) {
          return {
            error: true,
            message: 'Image should not exceed 5MB.',
          };
        }

        return this.checkFileFormat(files[0].name, allTypes, imageFormats);
      case THREE:
        if (file.size > megaBytesToBytes(50)) {
          return {
            error: true,
            message: '3D files should not exceed 50MB.',
          };
        }

        return this.checkFileFormat(files[0].name, allTypes, threeFormats);
      case VIDEO:
        if (file.size > megaBytesToBytes(20)) {
          return {
            error: true,
            message: 'Video files should not exceed 20MB.',
          };
        }

        return this.checkFileFormat(files[0].name, allTypes, videoFormats);
      case DOCS:
        if (file.size > megaBytesToBytes(5)) {
          return {
            error: true,
            message: 'Document files should not exceed 5MB.',
          };
        }

        return this.checkFileFormat(files[0].name, allTypes, documentFormats);
      default:
        return null;
    }
  }

  checkFileFormat(fileName, allFormats, typeFormats) {
    const extension = path.extname(fileName).replace('.', '').toLowerCase();
    if (allFormats.indexOf(extension) === -1) {
      return {
        error: true,
        message: 'Sorry, the uploaded format is not supported.',
      };
    }
    if (typeFormats.indexOf(extension) === -1) {
      return {
        error: true,
        message: 'Please add a file format that matches the type of resource you are creating.',
      };
    }
    return null;
  }

  async loadInitialData() {
    await this.materialRepository.getAnatomies();
    await this.materialRepository.getModalities();
    this.criteria$.subscribe((criteria) => this.materialRepository.getMaterials(criteria));
  }

  updateCriteria(updatedCriteria) {
    this.criteria$.next({
      ...this.criteria$.getValue(),
      ...updatedCriteria,
    });
  }

  refreshCriteria() {
    this.newResources$.next([]);
    this.criteria$.next(DEFAULT_CRITERIA);
  }

  get tags$() {
    return this.materialRepository.tags$;
  }

  get materials$() {
    return this.materialRepository.materials$;
  }

  get modalities$() {
    return this.materialRepository.modalities$;
  }

  get anatomies$() {
    return this.materialRepository.anatomies$;
  }

  get materialsLoading$() {
    return this.materialRepository.loading$;
  }

  deleteMaterial(material) {
    if (!material.isReady) {
      this.errorRepository.error$.next({
        message: 'Failed to delete file, this resource is not ready!',
        severity: 'error',
      });
      return;
    }

    const isMediphanyWorkspace = this.userWorkspace$.getValue()?.isMediphanyWorkspace;

    if (!isMediphanyWorkspace && material?.privacy === resourcePrivacy.GLOBAL) {
      this.errorRepository.error$.next({
        message: "You cannot delete 'Global Mediphany' resource",
        severity: 'error',
      });
      return;
    }

    this.dialogRepository.loadDialog(ConfirmDialog, {
      cancelBtnAction: () => this.dialogRepository.toggleDialog(false),
      cancelBtnText: 'Cancel',
      message: 'Are you sure you want to delete this file?',
      okBtnAction: async () => {
        await this.materialRepository.deleteMaterial(material.id);
        await this.getMaterials();
        this.dialogRepository.toggleDialog(false);
      },
      okBtnText: 'Delete',
      title: 'Delete file?',
    });
  }

  async openViewer(material, dialogClasses = null, props) {
    if (!material.isReady) {
      this.errorRepository.error$.next({
        message: 'Failed to open file, the resource is not ready!',
        severity: 'error',
      });
      return;
    }

    if (material.type === IMAGE) {
      this.dialogRepository.loadDialog(ImageWrapper, {
        dialogClasses,
        onClose: () => {
          this.dialogRepository.toggleDialog(false);
        },
        url: material.path,
      });
    }
    if (material.type === VIDEO) {
      const videoUrl = material?.h264Path || material.path;
      this.dialogRepository.loadDialog(VideoWrapper, { url: videoUrl });
    }

    if (material.type === THREE) {
      this.dialogRepository.loadDialog(ObjViewer, { url: material.path });
    }

    if (material.type === DCM) {
      try {
        // Should reload the resource info because the viewerLink may have expired
        const resourceInfo = await this.getResourceByUuid(material.resource.uuid);

        this.appRepository.handleOpenLoading();

        let childResources = [];
        if (resourceInfo?.id) {
          childResources = await this.patientRepository.getChildResourcesLink(resourceInfo.id);
        }

        const title = resourceInfo?.description || '';

        this.dialogRepository.loadDialog(AmbraContainer, {
          childResources,
          onClose: () => {
            this.dialogRepository.toggleDialog(false);
            if (props?.onClose) props.onClose();
          },
          title,
          url: resourceInfo?.viewerLink,
        });
      } finally {
        this.appRepository.handleCloseLoading();
      }
    }

    if (material.type === DOCS) {
      this.dialogRepository.loadDialog(PdfViewer, { title: material?.title || '', url: material?.resource?.path });
    }
  }

  async getResourceByUuid(uuid) {
    try {
      this.loadingResourceInfo$.next(uuid);
      return await this.resourceRepository.getResourceInfo(uuid);
    } finally {
      this.loadingResourceInfo$.next(null);
    }
  }

  get materialCount$() {
    return this.materialRepository.materialCount$;
  }

  get userDetails$() {
    return this.userRepository.userDetails$;
  }

  listenToResourceUpdate() {
    return this.notificationRepository.getSocketConnection$().pipe(
      distinctUntilChanged((a, b) => a === b),
      switchMap((connected) => {
        if (connected) {
          this.notificationRepository.sendMessage('join-resource-room');
          return this.notificationRepository.subscribeChannel('resource-updated');
        }

        return EMPTY;
      }),
      tap(({ id = 0 }) => {
        const newResources = this.newResources$.getValue();
        if (!newResources.includes(id)) {
          this.newResources$.next([...newResources, id]);
        }
      })
    );
  }
}
